import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Sparkles, Trash2, Eye, EyeOff, ChevronDown, Pencil } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { ArticleEditor } from './ArticleEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const ArticleRow = ({ a, onUpdate, onDelete, onSave }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const published = a.status === 'published';
  return (
    <div data-testid={`admin-article-${a.article_id}`} className="bg-[#FBF7F0] border border-[#1E1B18]/8 rounded-sm p-5">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <button onClick={() => setOpen(!open)} data-testid={`admin-article-toggle-${a.article_id}`} className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-3 mb-1">
            <span className={`text-[10px] tracking-[0.15em] font-medium px-2.5 py-0.5 rounded-full uppercase ${published ? 'text-[#2F7A4E] bg-[#DDEEE1]' : 'text-[#8B7FB8] bg-[#E5DDF0]'}`}>{a.status}</span>
            <span className="text-[12px] text-[#1E1B18]/50">{a.created_at.slice(0, 10)}</span>
          </div>
          <div className="text-[15px] font-medium text-[#1E1B18] flex items-center gap-2">
            {a.title} <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>
        <div className="flex gap-2 shrink-0">
          <button data-testid={`admin-article-edit-${a.article_id}`} onClick={() => { setEditing(!editing); setOpen(false); }}
            className="inline-flex items-center gap-1.5 border border-[#1E1B18]/20 hover:border-[#1E1B18]/50 text-[#1E1B18] px-4 py-2 rounded-full text-[12px] font-medium transition-colors">
            <Pencil size={13} /> Edit
          </button>
          <button data-testid={`admin-article-publish-${a.article_id}`} onClick={() => onUpdate(a.article_id, published ? 'draft' : 'published')}
            className="inline-flex items-center gap-1.5 bg-[#0F0E0C] hover:bg-[#2A2622] text-[#F4EFE7] px-4 py-2 rounded-full text-[12px] font-medium transition-colors">
            {published ? <><EyeOff size={13} /> Unpublish</> : <><Eye size={13} /> Approve & publish</>}
          </button>
          <button data-testid={`admin-article-delete-${a.article_id}`} onClick={() => onDelete(a.article_id)}
            className="inline-flex items-center gap-1.5 border border-[#1E1B18]/20 hover:border-[#1E1B18]/50 text-[#1E1B18] px-4 py-2 rounded-full text-[12px] font-medium transition-colors">
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>
      {editing && <ArticleEditor a={a} onCancel={() => setEditing(false)} onSave={async (id, p) => { if (await onSave(id, p)) setEditing(false); }} />}
      {open && !editing && (
        <div className="mt-4 pt-4 border-t border-[#1E1B18]/10 text-[13px] text-[#1E1B18]/75 space-y-3">
          <p className="italic">{a.meta_description}</p>
          {a.sections.map((s, i) => (
            <div key={i}><p className="font-medium text-[#1E1B18]">{s.heading}</p>{s.paragraphs.map((p, j) => <p key={j} className="mt-1">{p}</p>)}</div>
          ))}
          <p className="text-[11px] text-[#1E1B18]/45">Keywords: {a.keywords.join(', ')}</p>
        </div>
      )}
    </div>
  );
};

export const AdminArticles = () => {
  const { toast } = useToast();
  const [articles, setArticles] = useState([]);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try { setArticles((await axios.get(`${API}/articles/admin/all`, opts)).data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/articles/admin/generate`, {}, opts);
      toast({ title: 'New draft ready', description: 'Review it below, then approve to publish.' });
      load();
    } catch { toast({ title: 'Generation failed, try again', variant: 'destructive' }); }
    finally { setGenerating(false); }
  };
  const update = async (id, status) => {
    try {
      await axios.patch(`${API}/articles/admin/${id}`, { status }, opts);
      toast({ title: status === 'published' ? 'Published' : 'Moved to drafts' });
      load();
    } catch { toast({ title: 'Update failed', variant: 'destructive' }); }
  };
  const saveEdits = async (id, payload) => {
    try {
      await axios.patch(`${API}/articles/admin/${id}`, payload, opts);
      toast({ title: 'Changes saved' }); load(); return true;
    } catch { toast({ title: 'Save failed — title and description cannot be empty', variant: 'destructive' }); return false; }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try { await axios.delete(`${API}/articles/admin/${id}`, opts); load(); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  return (
    <section data-testid="admin-articles-section" className="pb-20 max-w-[1200px] mx-auto px-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-3 flex items-center gap-2"><FileText size={13} /> SEO ARTICLES</p>
          <h2 className="font-serif text-4xl text-[#1E1B18]">Daily article drafts</h2>
          <p className="text-[13px] text-[#1E1B18]/55 mt-2">A new draft is written every morning. Nothing goes live until you approve it. Live at /articles and /mississauga-rmt.</p>
        </div>
        <button data-testid="admin-generate-article-btn" onClick={generate} disabled={generating}
          className="inline-flex items-center gap-2 bg-[#8B7FB8] hover:bg-[#7B6BA6] disabled:opacity-60 text-white px-5 py-2.5 rounded-full text-[12px] font-medium transition-colors">
          <Sparkles size={13} className={generating ? 'animate-pulse' : ''} /> {generating ? 'Writing… (up to a minute)' : 'Write a draft now'}
        </button>
      </div>
      {articles.length === 0
        ? <div data-testid="admin-articles-empty" className="text-[14px] text-[#1E1B18]/60 py-12 text-center border border-dashed border-[#1E1B18]/15 rounded-sm bg-[#FBF7F0]">No drafts yet.</div>
        : <div className="space-y-3">{articles.map((a) => <ArticleRow key={a.article_id} a={a} onUpdate={update} onDelete={remove} onSave={saveEdits} />)}</div>}
    </section>
  );
};
